/**
 * Scheduled Workflows Service
 * Handles CRUD operations and control actions for scheduled workflows
 */

import { TeamScopedService } from './base.service';

export interface ScheduledWorkflow {
  workflowId: string;
  teamId: string;
  agentId: string;
  name: string;
  description: string;
  enabled: boolean;
  type: 'one-off' | 'repeating';
  schedule: {
    type: 'one-off' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'cron';
    expression?: string;
    executeAt?: string;
    timezone: string;
  };
  trigger: {
    prompt: string;
    systemMessage: string;
    todoTemplate: Array<{
      description: string;
      requiresApproval?: boolean;
    }>;
  };
  primaryModel?: string;
  secondaryModel?: string;
  status: 'active' | 'paused' | 'error' | 'completed';
  nextRun?: string;
  lastRun?: {
    timestamp: string;
    status: 'success' | 'failed' | 'in_progress';
    todoListId?: string;
    error?: string;
  };
  metadata: {
    createdAt: string;
    updatedAt: string;
    createdBy: string;
  };
}

export interface CreateWorkflowInput {
  name: string;
  description: string;
  prompt: string;
  agentId: string;
  schedule: {
    type: 'one-off' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'cron';
    expression?: string;
    executeAt?: string;
    timezone: string;
  };
  enabled?: boolean;
  primaryModel?: string;
  secondaryModel?: string;
}

export interface UpdateWorkflowInput {
  name?: string;
  description?: string;
  prompt?: string;
  schedule?: {
    type: 'one-off' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'cron';
    expression?: string;
    executeAt?: string;
    timezone: string;
  };
  enabled?: boolean;
  primaryModel?: string;
  secondaryModel?: string;
}

export interface SupportedModel {
  id: string;
  label: string;
  provider: string;
  model: string;
}

export interface TodoItem {
  description: string;
  requiresApproval?: boolean;
}

export interface PreviewWorkflowOutput {
  todos: TodoItem[];
  estimatedDuration: string;
}

export interface WorkflowExecution {
  workflowId: string;
  teamId: string;
  scheduledFor: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  threadId?: string;
  executedAt?: string;
  duration?: number;
  error?: string;
}

class ScheduledWorkflowsService extends TeamScopedService {
  /**
   * Get supported AI models
   */
  async getSupportedModels(): Promise<SupportedModel[]> {
    const response = await this.get<{ models: SupportedModel[] }>('/supported-models');
    return response.models || [];
  }

  /**
   * List all scheduled workflows for a team
   */
  async listWorkflows(teamId: string): Promise<ScheduledWorkflow[]> {
    const response = await this.getTeam<{ workflows: ScheduledWorkflow[] }>(
      teamId,
      '/scheduled-workflows'
    );
    return response.workflows || [];
  }

  /**
   * Get a single scheduled workflow by ID
   */
  async getWorkflow(teamId: string, workflowId: string): Promise<ScheduledWorkflow> {
    const response = await this.getTeam<{ workflow: ScheduledWorkflow }>(
      teamId,
      `/scheduled-workflows/${workflowId}`
    );
    return response.workflow;
  }

  /**
   * Create a new scheduled workflow
   */
  async createWorkflow(teamId: string, input: CreateWorkflowInput): Promise<ScheduledWorkflow> {
    const response = await this.postTeam<{ workflowId: string; workflow: ScheduledWorkflow }>(
      teamId,
      '/scheduled-workflows',
      input
    );
    return response.workflow;
  }

  /**
   * Update an existing scheduled workflow
   */
  async updateWorkflow(
    teamId: string,
    workflowId: string,
    updates: UpdateWorkflowInput
  ): Promise<ScheduledWorkflow> {
    const response = await this.putTeam<{ workflow: ScheduledWorkflow }>(
      teamId,
      `/scheduled-workflows/${workflowId}`,
      updates
    );
    return response.workflow;
  }

  /**
   * Delete a scheduled workflow
   */
  async deleteWorkflow(teamId: string, workflowId: string): Promise<void> {
    await this.deleteTeam<void>(teamId, `/scheduled-workflows/${workflowId}`);
  }

  /**
   * Pause a scheduled workflow
   */
  async pauseWorkflow(teamId: string, workflowId: string): Promise<void> {
    await this.postTeam<void>(
      teamId,
      `/scheduled-workflows/${workflowId}/pause`,
      {}
    );
  }

  /**
   * Resume a paused workflow
   */
  async resumeWorkflow(teamId: string, workflowId: string): Promise<void> {
    await this.postTeam<void>(
      teamId,
      `/scheduled-workflows/${workflowId}/resume`,
      {}
    );
  }

  /**
   * Run a workflow immediately (on-demand)
   */
  async runWorkflow(teamId: string, workflowId: string): Promise<{ threadId: string }> {
    const response = await this.postTeam<{ execution: any; threadId: string }>(
      teamId,
      `/scheduled-workflows/${workflowId}/run`,
      {}
    );
    return { threadId: response.threadId };
  }

  /**
   * Get execution history for a workflow
   */
  async getWorkflowHistory(
    teamId: string,
    workflowId: string,
    limit?: number
  ): Promise<WorkflowExecution[]> {
    const path = limit
      ? `/scheduled-workflows/${workflowId}/history?limit=${limit}`
      : `/scheduled-workflows/${workflowId}/history`;

    const response = await this.getTeam<{ executions: WorkflowExecution[] }>(
      teamId,
      path
    );
    return response.executions || [];
  }

  /**
   * Preview TODO list that will be generated for a workflow
   * Returns threadId - subscribe to WebSocket to receive TODO list
   */
  async previewWorkflow(
    teamId: string,
    prompt: string,
    agentId: string
  ): Promise<{ threadId: string }> {
    return this.postTeam<{ threadId: string }>(
      teamId,
      '/scheduled-workflows/preview',
      { prompt, agentId }
    );
  }
}

export const scheduledWorkflowsService = new ScheduledWorkflowsService();
